import {gzip} from 'pako'

import {
  audioContentType,
  AudioExtension,
  audioFileExtensionsSet,
  contentType,
  NonAudioExtension,
  nonAudioFileExtensionsSet
} from './'

function isAudioExtension(ext: string): ext is AudioExtension {
  return audioFileExtensionsSet.has(ext)
}

function isNonAudioExtension(ext: string): ext is NonAudioExtension {
  return nonAudioFileExtensionsSet.has(ext)
}

export async function compressFile(file: File) {
  const compressed = gzip(await file.arrayBuffer())
  // Cast: TS 5.7+ makes Uint8Array generic, so Uint8Array<ArrayBufferLike> isn't assignable to BlobPart. Safe at runtime.
  return new File([compressed as BlobPart], file.name)
}

function getExtensionFromFilename(fileName: string) {
  return fileName.split('.').at(-1)!
}

export async function uploadFileFromPolicy(
  file: File,
  url: string,
  fields: Record<string, string>,
  compressed = false
) {
  const formData = new FormData()

  for (const [name, value] of Object.entries(fields)) {
    formData.append(name, value)
  }

  if (compressed) {
    formData.append('Content-Encoding', 'gzip')
  }

  const extension = getExtensionFromFilename(file.name)
  let type = 'application/octet-stream'
  if (isAudioExtension(extension)) {
    type = audioContentType(extension)
  } else if (isNonAudioExtension(extension)) {
    type = contentType(extension)
  }

  formData.append('Content-Type', type)

  formData.append('file', compressed ? await compressFile(file) : file)

  const response = await fetch(url, {
    method: 'POST',
    body: formData
  })
  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`)
  }
}

export async function computeSHA512(file: File) {
  const arrayBuffer = await file.arrayBuffer()
  const hashBuffer = await crypto.subtle.digest('SHA-512', arrayBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return hashHex
}
